import React, { memo, useMemo } from "react";
import { DataTypeProvider } from "@devexpress/dx-react-grid";
import pluralize from "pluralize";
import PropTypes from "prop-types";

import { Paper } from "../../../mui/core";
import Spec from "../../../../models/Spec";
import GridAPI from "../../../ui/Grid/GridAPI";
import { RowMenuProvider } from "../../../ui/Grid/utilComponents";
import * as dataTypeProviders from "../../../ui/Grid/utilComponents/dataTypeProviders";
import { ColumnWrapper } from "./components";
import { useHeaderComponent } from "../../../ui/BWGrid/GridHeader";
import propTypes from "../../../../constants/propTypes";
import RowComponent from "./gridComponents/RowComponent";
import { useGridProps } from "./gridProps";
import withSelectedRowsAPI from "../../../ui/BWGrid/withSelectedRowsAPI";
import { GridActions } from "./GridActions";
import { DescriptionTypeProvider } from "../../Specs/DescriptionColumn";

export const categoryTypeProvider = props => (
  <DataTypeProvider
    formatterComponent={({ value, row }) => {
      if (!value) return null;
      return <ColumnWrapper id={row.id} field={value} value={value} />;
    }}
    {...props}
    for={["specCategory.name", "spec.specCategory.name"]}
  />
);

export function isSpecReadOnly(id, values) {
  return values.__deleted__;
}

const includes = [
  "area",
  "specCategory",
  "purchaseOrder.vendor",
  "glCodes",
  "vendor",
  "shipments",
  "unitOfMeasure",
];

const Grid = ({
  dataComponent,
  purchaseOrder,
  purchaseOrderId,
  selectedProjectCurrency,
  onGoToSpecDetails,
  onOpenQuickEditModal,
  onOpenAddSpecModal,
  onRemoveSpec,
  isPOChangeDisabled,
}) => {
  const {
    rowProps,
    detailRowProps,
    gridActions,
    filteringDataTypeProviders,
    DXGrid,
    columns,
  } = useGridProps({
    purchaseOrderId,
    onOpenAddSpecModal,
    onGoToSpecDetails,
    dataComponent,
    onOpenQuickEditModal,
    onRemoveSpec,
    isPOChangeDisabled,
  });

  const showSelectionColumn = !isPOChangeDisabled;

  // Stabilize the references passed to the (memoized) grid so it doesn't
  // re-render every row whenever an unrelated store update re-renders this
  // component (e.g. a modal loading data). Keyed on primitive ids so they stay
  // stable across the fresh redux object references of dataComponent/purchaseOrder.
  const headerOverride = useMemo(
    () => (
      <GridActions
        dataComponentId={dataComponent.dataComponentId}
        purchaseOrder={purchaseOrder}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataComponent.dataComponentId, purchaseOrder && purchaseOrder.id]
  );

  const GridHeader = useHeaderComponent({
    headerText: pluralize("Item", dataComponent.totalRows, true),
    headerOverride,
    actions: gridActions,
  });

  const tableComponents = useMemo(
    () => ({ GridHeader, RowComponent, DXGrid }),
    [GridHeader, DXGrid]
  );

  const dataTypeProvidersList = useMemo(
    () => [
      RowMenuProvider,
      dataTypeProviders.numberTypeBuilder(
        ["totalQuantityWithoutOverage", "left", "shipmentTotalQty"],
        true
      ),
      dataTypeProviders.currencyTypeBuilder(
        ["price", "totalPriceWithoutOverage"],
        selectedProjectCurrency
      ),
      categoryTypeProvider,
      DescriptionTypeProvider,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedProjectCurrency && selectedProjectCurrency.id]
  );

  return (
    <Paper>
      <GridAPI
        apiRoute="specs"
        model={Spec}
        rowProps={rowProps}
        includes={includes}
        defaultSorting={[{ columnName: "customNumber", direction: "asc" }]}
        apiFilters={{
          rootFilters: {
            $where: {
              purchaseOrderId,
            },
          },
          sort: [{ columnName: "customNumber", direction: "asc" }],
          params: {
            omitDefaultModifier: true,
            modifiers: [
              "withQuantityPrice",
              "withTotalQuantityPaid",
              "withTotalQuantityShipped",
            ],
          },
        }}
        dataComponent={dataComponent}
        tableComponents={tableComponents}
        dxGridProps={detailRowProps.dxGridProps}
        columns={columns}
        columnExtensions={[]}
        filteringDataTypeProviders={filteringDataTypeProviders}
        dataTypeProviders={dataTypeProvidersList}
        showSelectAll={showSelectionColumn}
        showSelectionColumn={showSelectionColumn}
      />
    </Paper>
  );
};

Grid.propTypes = {
  dataComponent: propTypes.dataComponent.isRequired,
  purchaseOrder: propTypes.purchaseOrder.isRequired,
  purchaseOrderId: PropTypes.string,
  onGoToSpecDetails: PropTypes.func.isRequired,
  onOpenQuickEditModal: PropTypes.func.isRequired,
  onOpenAddSpecModal: PropTypes.func.isRequired,
  onRemoveSpec: PropTypes.func.isRequired,
  isPOChangeDisabled: PropTypes.bool.isRequired,
  onOpenEditAction: PropTypes.func,
  selectedProjectCurrency: PropTypes.object,
};

export default memo(withSelectedRowsAPI(Grid));
